import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, Sparkles, Mail, Bell, Send } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EditorWorkshop } from "@/components/EditorWorkshop";
import { ReportsQueue } from "@/components/ReportsQueue";
import { PersistentLarixKey } from "@/components/PersistentLarixKey";
import { TestLivePing } from "@/components/TestLivePing";
import { InvitedTesters } from "@/components/InvitedTesters";

const docs = [
  {
    id: "howto-manual-jul19",
    title: "Le Salon · How-to Manual · Updated July 19, 2026",
    description:
      "The current edition — supersedes all previous how-to manuals. Now includes Section 12: Live Deployment (July 19, 2026), documenting the successful launch of salon.belleinspiration.com. Covers the two environments (preview vs. production), how the custom domain was connected via Entri + Wix, how to push future updates via Publish, and how to open the doors to the public when ready. Also carries every prior section: Larix persistent stream key, the LIVE banner, the Editor's Workshop, room-first posting, moderation queue, invited testers panel, and 61/61 regression tests.",
    file: "/docs/le-salon-howto-manual-july-19-2026.docx",
    format: "Word",
    badge: "Latest",
  },
  {
    id: "handbook",
    title: "Founder's Handbook",
    description:
      "The complete editor's manual for running Le Salon. Your tone, your editorial calendar, how to host a salon evening, how to onboard new members.",
    file: "/docs/le-salon-founders-handbook.pdf",
    format: "PDF",
  },
  {
    id: "prefix-domain",
    title: "lesalon___.com — One-word Descriptor Ideas",
    description:
      "Eight 'lesalon[word].com' options that say what Le Salon is, simply. My top pick: lesaloncircle.com — a name that carries 300 years of Parisian salon history in one word.",
    file: "/docs/le-salon-prefix-domain-ideas.docx",
    format: "Word",
  },
  {
    id: "domain-ideas",
    title: "Domain Name Ideas for an Eventual Standalone",
    description:
      "Ten curated .com options to consider as a future home for Le Salon — with my top two picks (bellesalon.com & chezlesalon.com) and where to buy them.",
    file: "/docs/le-salon-domain-name-ideas.docx",
    format: "Word",
  },
  {
    id: "subdomain-decision",
    title: "Subdomain vs Separate Domain — and the Real Cost",
    description:
      "My honest recommendation on lesalon.belleinspiration.com vs buying a separate lesalon.com, plus the clear answer on why a subdomain is $0 extra cost.",
    file: "/docs/le-salon-subdomain-decision.docx",
    format: "Word",
  },
  {
    id: "wix-connection",
    title: "Connecting to Belle Inspiration on Wix",
    description:
      "Honest answer to your 'one website to maintain' wish — why we can't truly embed inside Wix, and how the subdomain approach still gives you exactly what you wanted. Includes step-by-step Wix setup.",
    file: "/docs/le-salon-wix-connection.docx",
    format: "Word",
  },
  {
    id: "boutique-badges",
    title: "Live, Boutique & Badges — for when you're ready",
    description:
      "Deferred until post-launch (free access at launch). An honest walkthrough of three future revenue mechanisms — subscriber badges, the Boutique marketplace, and premium live rooms — what each looks like for you, for your members, and what it costs.",
    file: "/docs/le-salon-boutique-and-badges.docx",
    format: "Word",
    badge: "Future",
  },
];

const FounderPortal = () => {
  const { user, loading } = useAuth();
  const [pushTitle, setPushTitle] = useState("A moment in the salon");
  const [pushBody, setPushBody] = useState("");
  const [pushUrl, setPushUrl] = useState("/feed");
  const [sending, setSending] = useState(false);
  const [showPushDialog, setShowPushDialog] = useState(false);

  const sendNotification = async () => {
    if (!pushBody.trim()) {
      toast.error("Please write a short message first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/push/send", {
        title: pushTitle,
        body: pushBody,
        url: pushUrl,
      });
      toast.success(
        data.sent === 0
          ? "Notification ready — but no one is subscribed yet."
          : `Notification sent to ${data.sent} of ${data.subscribers} members.`
      );
      setShowPushDialog(false);
      setPushBody("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
    );
  }

  if (!user || !user.is_editor) {
    return <Navigate to="/" replace />;
  }

  return (
    <div data-testid="founder-portal-page" className="app-shell">
      <Navbar />

      {/* MASTHEAD */}
      <div style={{ background: "var(--bg-alt)", borderBottom: "1px solid var(--border-default)" }}>
        <div
          className="container-belle"
          style={{ paddingTop: 130, paddingBottom: 64, textAlign: "center", maxWidth: 880 }}
        >
          <div
            className="overline"
            style={{
              color: "var(--brand-accent)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "center",
            }}
          >
            <Sparkles size={14} strokeWidth={1.4} /> For the Editor's Eyes Only
          </div>
          <h1
            className="editorial-h1"
            style={{ fontSize: "clamp(44px, 6.5vw, 84px)", marginTop: 18, marginBottom: 20 }}
          >
            The Founder's Desk
          </h1>
          <p
            className="font-serif"
            style={{
              fontSize: 22,
              fontStyle: "italic",
              color: "var(--text-secondary)",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Every document, guide, and walkthrough I've prepared for you — kept safe in one place, always.
          </p>
          <p className="overline" style={{ marginTop: 24, opacity: 0.6 }}>
            Visible only to you, Amélie.
          </p>
        </div>
      </div>

      {/* DOCS LIST */}
      <div className="container-belle" style={{ paddingTop: 64, paddingBottom: 96, maxWidth: 880 }}>
        <style>{`
          .founder-doc-card {
            display: grid;
            grid-template-columns: auto 1fr auto;
            gap: 24px;
            align-items: center;
          }
          /* Stack the doc icon, description and Download button on phones
             so nothing overflows the 390px viewport. */
          @media (max-width: 767px) {
            .founder-doc-card {
              grid-template-columns: 1fr;
              gap: 16px;
            }
            .founder-doc-card > a.btn-accent { justify-self: flex-start; }
          }
        `}</style>
        <ReportsQueue />
        <InvitedTesters />
        <PersistentLarixKey />
        <TestLivePing />
        <EditorWorkshop />

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {docs.map((doc) => (
            <article
              key={doc.id}
              data-testid={`founder-doc-${doc.id}`}
              className="founder-doc-card"
              style={{
                border: "1px solid var(--border-default)",
                background: "var(--surface)",
                padding: 32,
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  background: "var(--bg-alt)",
                  display: "grid",
                  placeItems: "center",
                  border: "1px solid var(--border-default)",
                }}
              >
                <FileText size={26} strokeWidth={1.3} color="var(--brand-accent)" />
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 6,
                  }}
                >
                  <h2
                    className="editorial-h2"
                    style={{ fontSize: 24, margin: 0, color: "var(--text-primary)" }}
                  >
                    {doc.title}
                  </h2>
                  {doc.badge && (
                    <span
                      className="overline"
                      style={{
                        background: "var(--brand-accent)",
                        color: "var(--surface)",
                        padding: "3px 10px",
                        fontSize: 10,
                        letterSpacing: "0.12em",
                      }}
                    >
                      {doc.badge}
                    </span>
                  )}
                  <span
                    className="overline"
                    style={{ color: "var(--text-muted)", fontSize: 11, opacity: 0.7 }}
                  >
                    {doc.format}
                  </span>
                </div>
                <p
                  className="editorial-body"
                  style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.55 }}
                >
                  {doc.description}
                </p>
              </div>

              <a
                href={doc.file}
                download
                data-testid={`download-${doc.id}`}
                className="btn-accent"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  whiteSpace: "nowrap",
                }}
              >
                <Download size={14} strokeWidth={1.5} /> Download
              </a>
            </article>
          ))}
        </div>

        {/* SEND NOTIFICATION */}
        <div
          data-testid="founder-send-notification"
          style={{
            marginTop: 64,
            padding: 40,
            background: "var(--surface)",
            border: "1px solid var(--border-default)",
          }}
        >
          <div
            className="overline"
            style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8 }}
          >
            <Bell size={12} strokeWidth={1.5} /> Editor's Tools
          </div>
          <h2
            className="editorial-h2"
            style={{ fontSize: 28, marginTop: 10, marginBottom: 8, color: "var(--text-primary)" }}
          >
            Whisper to the salon.
          </h2>
          <p
            className="editorial-body"
            style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.6, marginBottom: 20 }}
          >
            Send a gentle push notification to every member who has opted in — for a new editor's
            letter, a live broadcast about to begin, or a moment worth coming home to. Use
            sparingly.
          </p>
          <button
            onClick={() => setShowPushDialog(true)}
            data-testid="open-send-notification"
            className="btn-accent"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <Send size={14} strokeWidth={1.5} /> Compose a notification
          </button>
        </div>

        {showPushDialog && (
          <div
            data-testid="send-notification-dialog"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(28,25,23,0.6)",
              zIndex: 200,
              display: "grid",
              placeItems: "center",
              padding: 24,
            }}
          >
            <div
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-default)",
                maxWidth: 520,
                width: "100%",
                padding: 40,
              }}
            >
              <div className="overline" style={{ color: "var(--brand-accent)" }}>
                A whisper to the salon
              </div>
              <h2
                className="editorial-h1"
                style={{ fontSize: 32, marginTop: 12, marginBottom: 20 }}
              >
                Compose notification.
              </h2>

              <label className="overline" style={{ display: "block" }}>
                Title
              </label>
              <input
                data-testid="push-title-input"
                value={pushTitle}
                onChange={(e) => setPushTitle(e.target.value)}
                className="input-belle"
                style={{ marginBottom: 20 }}
              />

              <label className="overline" style={{ display: "block" }}>
                Message
              </label>
              <textarea
                data-testid="push-body-input"
                value={pushBody}
                onChange={(e) => setPushBody(e.target.value)}
                className="input-belle"
                rows={3}
                style={{
                  minHeight: 90,
                  resize: "vertical",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 18,
                  fontStyle: "italic",
                  marginBottom: 20,
                }}
                placeholder="A new letter awaits in Le Salon…"
              />

              <label className="overline" style={{ display: "block" }}>
                Open this page when tapped
              </label>
              <input
                data-testid="push-url-input"
                value={pushUrl}
                onChange={(e) => setPushUrl(e.target.value)}
                className="input-belle"
                style={{ marginBottom: 24 }}
                placeholder="/feed"
              />

              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={sendNotification}
                  disabled={sending}
                  className="btn-accent"
                  data-testid="confirm-send-notification"
                >
                  {sending ? "Sending…" : "Send to all subscribers"}
                </button>
                <button
                  onClick={() => setShowPushDialog(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GENTLE NOTE */}
        <div
          style={{
            marginTop: 64,
            padding: 32,
            background: "var(--bg-alt)",
            border: "1px solid var(--border-default)",
            textAlign: "center",
          }}
        >
          <Mail
            size={20}
            strokeWidth={1.3}
            color="var(--brand-accent)"
            style={{ marginBottom: 12 }}
          />
          <p
            className="font-serif"
            style={{
              fontSize: 18,
              fontStyle: "italic",
              color: "var(--text-secondary)",
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 560,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            New documents will appear here whenever I prepare one for you. Bookmark this page —
            <strong style={{ color: "var(--text-primary)" }}> {window.location.origin}/founder</strong>
            — and nothing can ever be lost again.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FounderPortal;
